import { LabeledInput } from '@gorila-shared-ui/components';
import { useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { allSubAccountsState } from '../../../storage/subAccounts';
import { changeUserPasswordState, editingUserState } from '../../../storage/users';
import { Permissions } from '../../../types/permissions';
import { InternalUser } from '../../../types/user';
import { PermissionTree } from '../../shared/permissionTree/PermissionTree';
import { LabeledEmailInput } from '../../ui/LabeledEmailInput';
import { LabeledSelect } from '../../ui/LabeledSelect';
import { UserPasswordForm } from './UserPasswordForm';

type Props = {
  setIsValidEmail: (isValid: boolean) => void;
  resetEmailField: boolean;
};
export function UserForm({ setIsValidEmail, resetEmailField }: Readonly<Props>) {
  const [editingUser, setEditingUser] = useRecoilState(editingUserState);
  const changePassword = useRecoilValue(changeUserPasswordState);
  const isNewUser = editingUser?._id === '';
  const subAccounts = useRecoilValue(allSubAccountsState);

  const subAccountOptions = useMemo(() => {
    if (!subAccounts) return;
    return [
      {
        id: '',
        label: 'Sin subcuenta',
      },
      ...subAccounts.map((subAccount) => ({ id: subAccount._id, label: subAccount.name })),
    ];
  }, [subAccounts]);

  if (!editingUser) return null;

  const onInputChange = (value: any, field: keyof InternalUser) => {
    setEditingUser((prev) => ({ ...prev!, [field]: value }));
  };

  const onPermissionsChange = (permissions: Permissions) => {
    setEditingUser((prev) => ({ ...prev!, permissions }));
  };

  if (changePassword) {
    return <UserPasswordForm />;
  }

  return (
    <>
      <LabeledInput
        label="Nombre de usuario"
        value={editingUser.username}
        onChange={(value) => onInputChange(value.trim(), 'username')}
        disabled={!isNewUser}
        required
      />
      <LabeledInput
        label="Nombre"
        value={editingUser.name}
        onChange={(value) => onInputChange(value, 'name')}
      />
      <LabeledEmailInput
        value={editingUser.email}
        onChange={(email) => onInputChange(email, 'email')}
        isEmailValid={setIsValidEmail}
        reset={resetEmailField}
        required
      />
      {isNewUser && <UserPasswordForm />}
      <LabeledSelect
        label="Subcuenta"
        options={subAccountOptions}
        value={[{ id: editingUser.subAccount?._id || '' }]}
        onChange={(params) => {
          if (params.option?.id ) {
            onInputChange({
              _id: params.option.id,
              name: params.option.label,
            }, 'subAccount')
          } else {
            onInputChange(undefined, 'subAccount')
          }
        }}
        disabled={!isNewUser}
      />
      <div>
        <PermissionTree
          userId={editingUser._id}
          userPermissions={editingUser.permissions}
          onChangePermissions={onPermissionsChange}
          disabled={false}
          useFilteredPermission={!!editingUser.subAccount}
        />
      </div>
    </>
  );
}
